import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminDashboard";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Users, Phone, Calendar, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function AdminCustomers() {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerAppointments, setCustomerAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${API}/admin/customers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomers(response.data);
      } catch (error) {
        toast.error("Müşteriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [token]);

  const fetchCustomerAppointments = async (customerId) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerAppointments(response.data);
    } catch (error) {
      toast.error("Randevular yüklenemedi");
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerAppointments(customer.id);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { class: "status-pending", text: "Bekliyor" },
      approved: { class: "status-approved", text: "Onaylandı" },
      rejected: { class: "status-rejected", text: "Reddedildi" },
      completed: { class: "status-completed", text: "Tamamlandı" },
      cancelled: { class: "status-cancelled", text: "İptal" }
    };
    const statusInfo = statusMap[status] || { class: "", text: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const filteredCustomers = customers.filter(c => 
    (c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
     c.phone.includes(searchTerm))
  );

  return (
    <AdminLayout title="Müşteri Yönetimi">
      <div data-testid="admin-customers">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer List */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#e5e7eb] rounded-xl p-4 shadow-sm">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
                <Input
                  placeholder="Müşteri ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-elite pl-10"
                  data-testid="customer-search"
                />
              </div>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="spinner"></div>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredCustomers.map((customer) => (
                    <button
                      key={customer.id}
                      onClick={() => handleSelectCustomer(customer)}
                      className={`w-full text-left p-4 border rounded-lg transition-colors ${
                        selectedCustomer?.id === customer.id
                          ? "border-[#c9a96e] bg-[#c9a96e]/10"
                          : "border-[#e5e7eb] hover:border-[#c9a96e]/50"
                      }`}
                      data-testid={`customer-${customer.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#c9a96e]/20 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-[#c9a96e]" />
                        </div>
                        <div>
                          <p className="text-[#1a1a2e] font-medium">{customer.name || "İsimsiz"}</p>
                          <p className="text-[#9ca3af] text-sm">{customer.phone}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Customer Details */}
          <div className="lg:col-span-2">
            {selectedCustomer ? (
              <div className="space-y-6">
                {/* Customer Info Card */}
                <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 shadow-sm">
                  <h3 className="font-display text-xl text-[#1a1a2e] mb-4">Müşteri Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-[#c9a96e]" />
                      <div>
                        <p className="text-[#9ca3af] text-sm">Ad Soyad</p>
                        <p className="text-[#1a1a2e]">{selectedCustomer.name || "Belirtilmemiş"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#c9a96e]" />
                      <div>
                        <p className="text-[#9ca3af] text-sm">Telefon</p>
                        <p className="text-[#1a1a2e]">{selectedCustomer.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#c9a96e]" />
                      <div>
                        <p className="text-[#9ca3af] text-sm">Kayıt Tarihi</p>
                        <p className="text-[#1a1a2e]">
                          {format(new Date(selectedCustomer.created_at), "d MMMM yyyy", { locale: tr })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Appointments */}
                <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 shadow-sm">
                  <h3 className="font-display text-xl text-[#1a1a2e] mb-4">
                    Randevu Geçmişi ({customerAppointments.length})
                  </h3>

                  {customerAppointments.length === 0 ? (
                    <p className="text-[#6b7280] text-center py-8">Randevu bulunamadı</p>
                  ) : (
                    <div className="space-y-3">
                      {customerAppointments.map((appointment) => (
                        <div 
                          key={appointment.id}
                          className="flex items-center justify-between p-4 bg-[#fafafa] border border-[#f3f4f6] rounded-lg"
                        >
                          <div>
                            <p className="text-[#1a1a2e]">{appointment.service_name}</p>
                            <p className="text-[#9ca3af] text-sm">
                              {format(new Date(appointment.date), "d MMM yyyy", { locale: tr })} - {appointment.time}
                            </p>
                            <p className="text-[#9ca3af] text-sm">{appointment.barber_name}</p>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(appointment.status)}
                            <p className="text-[#c9a96e] font-accent mt-1">{appointment.service_price}₺</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-[#e5e7eb] rounded-xl p-12 text-center shadow-sm">
                <Users className="w-12 h-12 text-[#9ca3af] mx-auto mb-4" />
                <p className="text-[#6b7280]">Detayları görüntülemek için bir müşteri seçin</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
